import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { FadeUp, SlideLeft, SlideRight, ScalePop, CountUp } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill, useEditMode } from '../../components';

function AboutCollage({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const { editMode } = useEditMode();
  const images = data.images || [];
  const updateImage = (idx, src, alt) => {
    const next = images.map((im, i) => (i === idx ? { src, alt } : im));
    update({ images: next });
  };
  const ctas = data.ctas || [];
  const updateCta = (i, patch) => {
    const next = ctas.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    update({ ctas: next });
  };

  return (
    <section id="about" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="absolute top-20 left-10 w-64 h-64 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(161,201,164,0.4) 1.5px, transparent 1.5px)', backgroundSize: '20px 20px' }} />

      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-16">
          <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4 text-center">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
        </FadeUp>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <SlideLeft>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-40 h-40 opacity-50 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(161,201,164,0.45) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
              <div className="relative grid grid-cols-2 gap-4">
                {images[0] && (
                  <EditableImage src={images[0].src} alt={images[0].alt} onChange={(src, alt) => updateImage(0, src, alt)} className="rounded-3xl shadow-soft h-72 w-full object-cover hover:scale-[1.03] transition-transform duration-500" />
                )}
                {images[1] && (
                  <EditableImage src={images[1].src} alt={images[1].alt} onChange={(src, alt) => updateImage(1, src, alt)} className="rounded-3xl shadow-soft h-72 w-full object-cover mt-12 hover:scale-[1.03] transition-transform duration-500" />
                )}
                {images[2] && (
                  <EditableImage src={images[2].src} alt={images[2].alt} onChange={(src, alt) => updateImage(2, src, alt)} className="rounded-3xl shadow-soft h-56 w-full object-cover col-span-2 -mt-8 hover:scale-[1.02] transition-transform duration-500" />
                )}
              </div>
              <ScalePop>
                <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-[0_20px_50px_-15px_rgba(46,58,24,0.4)] px-6 py-4 flex items-center gap-4 border border-olive/10">
                  <div className="font-display text-3xl font-bold text-olive-deep">
                    {editMode ? (
                      <span className="inline-flex items-center gap-1">
                        <EditableText
                          value={String(data.stat?.value ?? '')}
                          onChange={(t) => update({ stat: { ...(data.stat || {}), value: t } })}
                          as="span"
                        />
                        <EditableText
                          value={data.stat?.suffix || ''}
                          onChange={(t) => update({ stat: { ...(data.stat || {}), suffix: t } })}
                          as="span"
                        />
                      </span>
                    ) : (
                      <CountUp to={Number(data.stat?.value) || 0} suffix={data.stat?.suffix || ''} />
                    )}
                  </div>
                  <EditableText
                    value={data.stat?.label}
                    onChange={(t) => update({ stat: { ...(data.stat || {}), label: t } })}
                    as="div"
                    multiline
                    className="text-sm leading-tight text-ink/70 whitespace-pre-line"
                  />
                </div>
              </ScalePop>
            </div>
          </SlideLeft>

          <SlideRight>
            <div className="flex items-center gap-1.5 mb-4">
              {[0, 1, 2, 3].map((d) => (
                <span key={d} className="block h-1.5 w-1.5 rounded-full bg-olive/40" />
              ))}
            </div>
            <h3 className="font-display text-3xl lg:text-4xl text-ink leading-tight">
              <EditableText value={data.subheading} onChange={(t) => update({ subheading: t })} as="span" multiline />
            </h3>
            <p className="mt-5 text-ink/70 leading-relaxed text-lg">
              <EditableText value={data.body} onChange={(t) => update({ body: t })} as="span" multiline />
            </p>
            <ul className="mt-7 space-y-3.5">
              {(data.bullets || []).map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-start gap-3 text-ink/80"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-olive text-white shrink-0 mt-0.5">
                    <CheckCircle2 size={14} />
                  </span>
                  <EditableText
                    value={b}
                    onChange={(t) => {
                      const next = [...(data.bullets || [])];
                      next[i] = t;
                      update({ bullets: next });
                    }}
                    as="span"
                  />
                </motion.li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              {ctas.map((c, i) => {
                const isPrimary = (c.variant || 'primary') === 'primary';
                return (
                  <div key={i} className="inline-flex flex-col">
                    <Link
                      to={c.href || '#'}
                      className={
                        isPrimary
                          ? 'group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-olive text-white font-semibold text-sm hover:bg-olive-deep hover:-translate-y-0.5 hover:shadow-glow-olive transition-all duration-300'
                          : 'inline-flex items-center gap-2 px-6 py-3 rounded-full border border-olive/30 text-olive-deep font-semibold text-sm hover:bg-olive-light hover:border-olive/60 transition-all duration-300'
                      }
                    >
                      <EditableText value={c.label} onChange={(t) => updateCta(i, { label: t })} as="span" />
                      {isPrimary && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
                    </Link>
                    <HrefPill value={c.href} onChange={(h) => updateCta(i, { href: h })} />
                  </div>
                );
              })}
            </div>
          </SlideRight>
        </div>
      </div>
    </section>
  );
}

export default AboutCollage;
